import type { LanguageCode } from './languages'

export type HomeHeroSlide = {
  id: string
  image: string
  srcSet?: string
  video?: string
  durationMs?: number | null
  eyebrow?: Record<LanguageCode, string>
  title?: Record<LanguageCode, string>
  copy?: Record<LanguageCode, string>
  alt: Record<LanguageCode, string>
  width: number
  height: number
  enabled: boolean
  order: number
}

export type HomeHeroSettings = {
  intervalMs: number
  slides: HomeHeroSlide[]
}

const alt = (tr: string, en: string, ru: string) => ({ tr, en, ru })
const localized = (tr: string, en: string, ru: string) => ({ tr, en, ru })
const emptyLocalized = () => ({ tr: '', en: '', ru: '' })
const normalizeLocalized = (value: Partial<Record<LanguageCode, string>> | undefined) => ({
  tr: value?.tr ?? '',
  en: value?.en ?? '',
  ru: value?.ru ?? '',
})

export const defaultHomeHeroSettings: HomeHeroSettings = {
  intervalMs: 7000,
  slides: [
    {
      id: 'modi-breakfast',
      image: '/legacy-media/wp-content/uploads/2025/02/kahvaltilar-1024x768.png',
      srcSet: '',
      alt: alt(
        'Modi Cafe kahvalti sunumu',
        'Modi Cafe breakfast service',
        'Завтрак в Modi Cafe',
      ),
      eyebrow: localized(
        'Yalova Termal Kahvalti',
        'Breakfast in Yalova Thermal',
        'Завтраки в Ялова Термал',
      ),
      title: localized(
        'Modi Cafe',
        'Modi Cafe',
        'Modi Cafe',
      ),
      copy: localized(
        'Gune taze kahvalti, sicak servis ve sakin bir butik cafe atmosferiyle baslayin.',
        'Start the day with fresh breakfast, warm service and a calm boutique cafe atmosphere.',
        'Начните день со свежего завтрака, теплого сервиса и уютной атмосферы бутикового кафе.',
      ),
      width: 1024,
      height: 768,
      video: '',
      enabled: true,
      order: 10,
    },
    {
      id: 'modi-hot-drinks',
      image: '/legacy-media/wp-content/uploads/2025/02/Sicak-icecekler-1024x768.png',
      srcSet: '',
      alt: alt(
        'Modi Cafe sicak icecekler',
        'Modi Cafe hot drinks',
        'Горячие напитки Modi Cafe',
      ),
      eyebrow: localized(
        'Kahve Molasi',
        'Coffee Break',
        'Кофейная пауза',
      ),
      title: localized(
        'Sicak kahve, sakin anlar',
        'Warm coffee, calm moments',
        'Теплый кофе и спокойные моменты',
      ),
      copy: localized(
        'Espresso, Turk kahvesi ve bitki caylariyla Termal gezinizde keyifli bir durak.',
        'A pleasant stop in Thermal with espresso, Turkish coffee and herbal teas.',
        'Приятная остановка в Термале с эспрессо, турецким кофе и травяными чаями.',
      ),
      width: 1024,
      height: 768,
      video: '',
      enabled: true,
      order: 20,
    },
    {
      id: 'modi-desserts',
      image: '/legacy-media/wp-content/uploads/2025/02/Tatli-1024x768.png',
      srcSet: '',
      alt: alt(
        'Modi Cafe tatli ve pastane lezzetleri',
        'Modi Cafe desserts and patisserie',
        'Десерты Modi Cafe',
      ),
      eyebrow: localized(
        'Tatli & Cafe',
        'Desserts & Cafe',
        'Десерты и кафе',
      ),
      title: localized(
        'Tatli bir mola',
        'A sweet pause',
        'Сладкая пауза',
      ),
      copy: localized(
        'Gunluk tatlilar, kahve eslesmeleri ve samimi servisle Yalova Termal molanizi tamamlayin.',
        'Complete your Yalova Thermal break with daily desserts, coffee pairings and friendly service.',
        'Дополните отдых в Ялова Термал десертами дня, кофе и дружелюбным сервисом.',
      ),
      width: 1024,
      height: 768,
      video: '',
      enabled: true,
      order: 30,
    },
  ],
}

const localStorageKey = 'modi_home_hero_settings'

const normalizeSettings = (settings: Partial<HomeHeroSettings> | null | undefined): HomeHeroSettings => ({
  intervalMs: settings?.intervalMs && settings.intervalMs >= 2500 ? settings.intervalMs : 7000,
  slides:
    settings?.slides && settings.slides.length > 0
      ? settings.slides
          .map((slide, index) => ({
            ...slide,
            id: slide.id || crypto.randomUUID(),
            order: slide.order ?? (index + 1) * 10,
            enabled: slide.enabled ?? true,
            video: slide.video ?? '',
            durationMs: slide.durationMs && slide.durationMs >= 1000 ? slide.durationMs : null,
            eyebrow: normalizeLocalized(slide.eyebrow),
            title: normalizeLocalized(slide.title),
            copy: normalizeLocalized(slide.copy),
            alt: normalizeLocalized(slide.alt ?? emptyLocalized()),
            srcSet: slide.srcSet ?? '',
          }))
          .sort((first, second) => first.order - second.order)
      : defaultHomeHeroSettings.slides,
})

function getLocalSettings() {
  try {
    const raw = window.localStorage.getItem(localStorageKey)
    return raw ? normalizeSettings(JSON.parse(raw) as HomeHeroSettings) : defaultHomeHeroSettings
  } catch {
    return defaultHomeHeroSettings
  }
}

function setLocalSettings(settings: HomeHeroSettings) {
  try {
    window.localStorage.setItem(localStorageKey, JSON.stringify(settings))
  } catch {
    // localStorage may be unavailable in private browser modes.
  }
}

export async function getHomeHeroSettings() {
  try {
    const firestoreModule = await import('firebase/firestore')
    const firebaseModule = await import('./firebase')
    const snapshot = await firestoreModule.getDoc(
      firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'homeHero'),
    )
    return snapshot.exists()
      ? normalizeSettings(snapshot.data() as HomeHeroSettings)
      : getLocalSettings()
  } catch {
    return getLocalSettings()
  }
}

export async function saveHomeHeroSettings(settings: HomeHeroSettings) {
  const normalized = normalizeSettings(settings)
  setLocalSettings(normalized)

  try {
    const firestoreModule = await import('firebase/firestore')
    const firebaseModule = await import('./firebase')
    await firebaseModule.ensureFirebaseAuth()
    await firestoreModule.setDoc(
      firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'homeHero'),
      normalized,
    )
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? `Hero ayarlari Firebase'e kaydedilemedi: ${error.message}`
        : 'Hero ayarlari Firebase kaydi basarisiz.',
      { cause: error },
    )
  }

  return normalized
}

export function subscribeHomeHeroSettings(listener: (settings: HomeHeroSettings) => void) {
  listener(getLocalSettings())

  let unsubscribe: (() => void) | undefined

  import('firebase/firestore')
    .then(async (firestoreModule) => {
      const firebaseModule = await import('./firebase')
      unsubscribe = firestoreModule.onSnapshot(
        firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'homeHero'),
        (snapshot) => {
          const settings = snapshot.exists()
            ? normalizeSettings(snapshot.data() as HomeHeroSettings)
            : getLocalSettings()
          listener(settings)
        },
        () => undefined,
      )
    })
    .catch(() => undefined)

  return () => unsubscribe?.()
}
